import { useMemo } from 'react';
import { match, P } from 'ts-pattern';
import { useIxcFundingV1SourceQueryServiceSourceList } from '@interxfi/api-client/generated/hooks/ixc-funding-v1-source-query-service-source-list';
import { useFlatData } from '@interxfi/api-client/utils/use-flat-data';
import type { FilterState } from '@interxfi/table-config/filter';
import type { TFunction } from '@interxfi/translation';
import {
  type IxcFundingV1AccountNumberSource,
  type IxcFundingV1AchSource,
  type IxcFundingV1DomesticWireSource,
  IxcFundingV1SourceQueryServiceSourceListFilterableFields,
  type IxcFundingV1SourceQueryServiceSourceListSourceListRequest,
  type IxcFundingV1SourceSpec,
  type IxcFundingV1SourceState,
  IxcFundingV1SourceStatus,
  type IxcFundingV1SourceTypeOneOfValue,
  type IxcFundingV1UsBankDetails,
} from '@interxfi/types/generated/api';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';

export function getUsBankDetailSummary(t: TFunction, bankDetails: IxcFundingV1UsBankDetails | undefined) {
  if (!bankDetails) {
    return undefined;
  }

  return [
    bankDetails.accountType
      ? t(`ixcFundingV1:enum.IxcFundingV1BankAccountType.${bankDetails.accountType}`, {
          defaultValue: bankDetails.accountType,
        })
      : undefined,
    bankDetails.accountNumber,
  ]
    .filter(Boolean)
    .join(' ');
}

export function getFundingDomesticWireSourceDetailSummary(t: TFunction, source: IxcFundingV1DomesticWireSource | undefined) {
  if (!source) {
    return undefined;
  }

  return getUsBankDetailSummary(t, source.bankDetails);
}

export function getFundingAchSourceDetailSummary(t: TFunction, source: IxcFundingV1AchSource | undefined) {
  if (!source) {
    return undefined;
  }

  return getUsBankDetailSummary(t, source.bankDetails);
}

export function getFundingAccountNumberSourceDetailSummary(t: TFunction, source: IxcFundingV1AccountNumberSource | undefined) {
  if (!source) {
    return undefined;
  }

  return getUsBankDetailSummary(t, source.bankDetails);
}

export function getFundingSourceDetailSummaryFromSpec(t: TFunction, spec: IxcFundingV1SourceSpec | undefined) {
  return match(spec)
    .with({ sourceType: { achSource: P.not(P.nullish) } }, (s) => getFundingAchSourceDetailSummary(t, s.sourceType.achSource))
    .with({ sourceType: { accountNumberSource: P.not(P.nullish) } }, (s) =>
      getFundingAccountNumberSourceDetailSummary(t, s.sourceType.accountNumberSource),
    )
    .with({ sourceType: { domesticWireSource: P.not(P.nullish) } }, (s) =>
      getFundingDomesticWireSourceDetailSummary(t, s.sourceType.domesticWireSource),
    )
    .otherwise(() => undefined);
}

export function getFundingSourceDetailSummary(t: TFunction, source: IxcFundingV1SourceState) {
  return getFundingSourceDetailSummaryFromSpec(t, source.data.spec);
}

export function getFullFundingSourceSummary(t: TFunction, source: IxcFundingV1SourceState) {
  return [source.data.spec.nickname, getFundingSourceDetailSummary(t, source)].filter(Boolean).join(' ');
}

export function getCompactFundingSourceSummaryFromSpec(spec: IxcFundingV1SourceSpec | undefined) {
  if (!spec?.sourceType) {
    return undefined;
  }

  if ('achSource' in spec.sourceType) {
    return [
      spec.nickname,
      spec.sourceType.achSource.bankDetails.accountNumber?.substring((spec.sourceType.achSource.bankDetails.accountNumber?.length || 0) - 4),
    ]
      .filter(Boolean)
      .join(' ');
  }

  if ('domesticWireSource' in spec.sourceType) {
    return [
      spec.nickname,
      spec.sourceType.domesticWireSource.bankDetails.accountNumber?.substring(
        (spec.sourceType.domesticWireSource.bankDetails.accountNumber?.length || 0) - 4,
      ),
    ]
      .filter(Boolean)
      .join(' ');
  }

  if ('accountNumberSource' in spec.sourceType) {
    return [
      spec.nickname,
      spec.sourceType.accountNumberSource.bankDetails?.accountNumber?.substring(
        (spec.sourceType.accountNumberSource.bankDetails.accountNumber?.length || 0) - 4,
      ),
    ]
      .filter(Boolean)
      .join(' ');
  }

  if ('plaidProcessorTokenSource' in spec.sourceType) {
    return [
      spec.nickname,
      spec.sourceType.plaidProcessorTokenSource.accountFromPlaid?.ach?.accountNumber?.substring(
        (spec.sourceType.plaidProcessorTokenSource.accountFromPlaid?.ach?.accountNumber.length || 0) - 4,
      ),
    ]
      .filter(Boolean)
      .join(' ');
  }

  if ('nickname' in spec && spec.nickname) {
    return spec.nickname;
  }

  exhaustiveCheck(spec.sourceType);

  return undefined;
}

export function buildFundingSourceListRequest(fields: {
  accountId?: string;
  isPrimary?: boolean;
  sourceType?: IxcFundingV1SourceTypeOneOfValue | IxcFundingV1SourceTypeOneOfValue[];
  status?: IxcFundingV1SourceStatus;
}): IxcFundingV1SourceQueryServiceSourceListSourceListRequest | undefined {
  const { accountId, isPrimary, sourceType, status } = fields;

  if (!accountId && !status && isPrimary === undefined && !sourceType) {
    return undefined;
  }

  return {
    query: {
      filters: [
        ...(accountId
          ? [
              {
                field: {
                  name: IxcFundingV1SourceQueryServiceSourceListFilterableFields.AccountId,
                  type: {
                    value: accountId,
                  },
                },
              },
            ]
          : []),
        ...(status
          ? [
              {
                field: {
                  name: IxcFundingV1SourceQueryServiceSourceListFilterableFields.Status,
                  type: {
                    value: status,
                  },
                },
              },
            ]
          : []),
        ...(isPrimary !== undefined
          ? [
              {
                field: {
                  name: IxcFundingV1SourceQueryServiceSourceListFilterableFields.DataSpecIsPrimary,
                  type: {
                    value: String(isPrimary),
                  },
                },
              },
            ]
          : []),
        ...(sourceType
          ? [
              {
                field: {
                  name: IxcFundingV1SourceQueryServiceSourceListFilterableFields.DataSpecSourceTypeType,
                  type: Array.isArray(sourceType) ? { in: { values: Array.from(new Set(sourceType)) } } : { value: sourceType },
                },
              },
            ]
          : []),
      ],
    },
  };
}

export function getListFundingSourcesFilters(fields: {
  accountId?: string;
  programId?: string;
  status?: IxcFundingV1SourceStatus;
  isPrimary?: boolean;
}): FilterState<IxcFundingV1SourceQueryServiceSourceListFilterableFields> {
  if (!Object.values(fields).some(Boolean)) {
    return [];
  }

  return [
    {
      type: {
        filters: [
          ...(fields.accountId
            ? [
                {
                  id: IxcFundingV1SourceQueryServiceSourceListFilterableFields.AccountId,
                  value: { exact: fields.accountId },
                },
              ]
            : []),
          ...(fields.programId
            ? [
                {
                  id: IxcFundingV1SourceQueryServiceSourceListFilterableFields.ProgramId,
                  value: { exact: fields.programId },
                },
              ]
            : []),
          ...(fields.status
            ? [
                {
                  id: IxcFundingV1SourceQueryServiceSourceListFilterableFields.Status,
                  value: { exact: fields.status },
                },
              ]
            : []),
          ...(fields.isPrimary !== undefined
            ? [
                {
                  id: IxcFundingV1SourceQueryServiceSourceListFilterableFields.DataSpecIsPrimary,
                  value: { exact: String(fields.isPrimary) },
                },
              ]
            : []),
        ],
      },
    },
  ];
}

export function useTryToFindPrimaryFundingSourceForAccount(accountId: string | undefined) {
  const request = useMemo(() => buildFundingSourceListRequest({ accountId, status: IxcFundingV1SourceStatus.Active, isPrimary: true }), [accountId]);
  const { data } = useIxcFundingV1SourceQueryServiceSourceList(request);
  const flatData = useFlatData(data, 'source');

  return useMemo(() => flatData?.find((source) => source.data.spec.isPrimary), [flatData]);
}
